/**
 * ErrorBoundary.js — top-level global React error boundary.
 *
 * Closes critical-rules.md HB-01 (Global React error boundary owed). Prior
 * state: only `frontend/src/pages/InterviewWizard.js` carried a component-
 * scoped boundary (`InterviewErrorBoundary` at :131-167); App.js + index.js
 * had no global boundary, so unhandled errors outside InterviewWizard
 * surfaced as blank screens.
 *
 * Wired in App.js wrapping <Router> so it catches render-phase errors from
 * every route tree (admin, supplier, interview portal, company). Component-
 * scoped boundaries take precedence on routes that have their own (e.g.,
 * InterviewWizard); this boundary is the fallback for everything else.
 *
 * Styling: inline only to avoid CSS coupling. Mirrors Wingman design system
 * tokens from docs/design-system.md (page background #F8F9FB, card #FFFFFF,
 * primary #2251FF, text primary #111827, text secondary #6B7280, border
 * #E2E5EA, 12px border-radius, 24px card padding).
 */

import React, { Component } from 'react';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    if (typeof console !== 'undefined' && console.error) {
      console.error('[ErrorBoundary] Uncaught render-phase error:', error, errorInfo);
    }
  }

  handleReload = () => {
    this.setState({ hasError: false });
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div
        style={{
          minHeight: '100vh',
          background: '#F8F9FB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
        }}
      >
        <div
          style={{
            background: '#FFFFFF',
            border: '1px solid #E2E5EA',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '480px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
          }}
        >
          <h1
            style={{
              fontSize: '20px',
              fontWeight: 600,
              color: '#111827',
              margin: '0 0 12px 0',
            }}
          >
            Something went wrong
          </h1>
          <p
            style={{
              fontSize: '14px',
              color: '#6B7280',
              lineHeight: 1.5,
              margin: '0 0 24px 0',
            }}
          >
            The application encountered an unexpected error. Reloading the page
            usually clears it. If the problem keeps happening, contact support
            with what you were doing when it appeared.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              background: '#2251FF',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
